/**
 * Environment gate for the Sanity adapter.
 *
 * DEFAULT-DENY: Sanity is only ever selected on an explicit allowlist of
 * environments that never reach customers: development, test and local.
 * Acceptance and production (the path to customers), or any unknown value,
 * return `false`. This lets the adapter be merged to `main` and exercised
 * locally, on development and on test without any risk of it activating on a
 * customer-facing environment. Test is on the list because that is where a
 * change is tested before it merges; acceptance is not, because whatever runs
 * there goes on to production.
 *
 * Pass `env('ENVIRONMENT')` (from `@eneco/common/env`). An empty string is
 * treated as local development (the env var is unset when running locally).
 *
 * `allowSharedEnvironment` is a per-container opt-in (default `false`): a
 * container that has explicitly decided it's ready to serve the `?_cms=`
 * override on acceptance/production (currently only `gb-eneco-main`, see
 * `containers/gb/eneco/main/cms-config.ts`) passes `true` here to additionally
 * allow those two shared environments. `FE_ENVIRONMENT` itself is set
 * DTAP-wide (identical for every container in a given tier), so this flag —
 * not the environment name — is what keeps the widening scoped to one
 * container.
 */
const ENABLED_ENVIRONMENTS = new Set(['development', 'test', 'local', '']);
const SHARED_ENVIRONMENTS = new Set(['acceptance', 'production']);

/** Environment-name gate — true on development/test/local/unset, or on acceptance/production when opted in. */
export const isSanityEnabled = (environment: string | undefined, allowSharedEnvironment = false): boolean => {
  const normalized = (environment ?? '').trim().toLowerCase();
  return ENABLED_ENVIRONMENTS.has(normalized) || (allowSharedEnvironment && SHARED_ENVIRONMENTS.has(normalized));
};

export interface SanityGuardInput {
  environment: string | undefined;
  projectId: string | undefined;
  dataset: string | undefined;
  /** See `isSanityEnabled` — per-container opt-in for acceptance/production. Default `false`. */
  allowSharedEnvironment?: boolean;
}

/**
 * The full guard a container should use to decide whether to inject the Sanity
 * adapter. Fail-safe: returns `true` ONLY when the environment is enabled AND
 * the required Sanity config is present. If the env vars are unset (the state
 * on `main` until someone configures them), this returns `false` and the
 * container falls back to Sitecore — so a partial rollout never breaks pages.
 */
export const shouldUseSanityAdapter = ({
  environment,
  projectId,
  dataset,
  allowSharedEnvironment,
}: SanityGuardInput): boolean =>
  isSanityEnabled(environment, allowSharedEnvironment) &&
  Boolean(projectId?.trim()) &&
  Boolean(dataset?.trim());
